import { FunnelLevel, levelOf, lossOf, ribbonColour, StepMetric } from "./funnelMath";
import { CentreSample, layoutRibbons, ribbonWidth, RibbonSpec } from "./funnelRibbons";

/*
 * What to draw, and where, for the funnel: pure (no DOM, no d3) so it can be tested. funnelLayer.ts draws it.
 */
export interface FunnelSlot {
  key: number;
  page: string;
  /** the slot box's CENTRE, in the grid's own coordinates */
  x: number;
  y: number;
}

export interface DrawItem {
  page: string;
  next: string;
  /** 1-based position in the funnel, and how many steps there are INCLUDING the goal */
  step: number;
  totalSteps: number;
  metric: StepMetric;
  loss: number;
  level: FunnelLevel;
  /** the ribbon's fill: by how much is lost, or the one neutral blue */
  colour: string;
  /** the closed SVG path of the ribbon, box edge to box edge, in the grid's own coordinates */
  d: string;
  /** how wide it is where it leaves this step's box and where it arrives at the next one */
  startW: number;
  endW: number;
  /** the ribbon's centre line (what a visitor riding it follows) and the centre of the box it arrives at */
  centre: CentreSample[];
  arriveAt: { x: number; y: number };
}

export interface BadgeItem {
  page: string;
  step: number;
  /** the box's top-left corner, where the step number sits */
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The pages the numbers are asked for, in order: the steps, then the goal (while picking there is no goal yet, and the last pick has no next step to measure). */
export const funnelPages = (steps: string[], goal: string | null, building: boolean): string[] => (building || !goal ? steps.slice() : [...steps, goal]);

/** A key for "which numbers are needed": it changes when a step is added or moved, or when the goal becomes known or changes, and then the numbers must be asked for AT ONCE, not at the next timer. */
export const pagesKey = (pages: string[]): string => pages.join("\u0001");

/** Is the "dim everything that is not the funnel" spotlight on? Not while picking (every box and visitor must be visible to click), and not with no funnel. */
export const spotlightActive = (steps: string[], building: boolean, dim: boolean): boolean => dim && !building && steps.length > 0;

/** The slot boxes that are NOT part of the funnel (and the catch-all "All other pages" box, if its key is given): what the spotlight dims, boxes and density clouds alike. */
export const nonFunnelKeys = (slots: FunnelSlot[], pages: string[], otherKey?: number): number[] => {
  const inFunnel = new Set(pages);
  const keys = slots.filter((s) => !inFunnel.has(s.page)).map((s) => s.key);

  return otherKey === undefined ? keys : [...keys, otherKey];
};

export interface BuildArgs {
  steps: string[];
  goal: string | null;
  building: boolean;
  slots: FunnelSlot[];
  boxW: number;
  boxH: number;
  metric: (page: string, next: string) => StepMetric | undefined;
  /** colour each ribbon by how much is lost at its step (otherwise one neutral blue) */
  byLoss?: boolean;
  /** dim every box that is not part of the funnel (steps and goal) */
  dim?: boolean;
}

export const buildFunnelItems = ({ steps, goal, building, slots, boxW, boxH, metric, byLoss = true, dim = false }: BuildArgs): { items: DrawItem[]; badges: BadgeItem[]; dimKeys: number[]; signature: string } => {
  const badges: BadgeItem[] = [];
  const totalSteps = steps.length + (goal && !building ? 1 : 0);
  const slotOf = (page: string) => slots.find((s) => s.page === page);
  const pairs: Array<{ page: string; next: string; step: number; m: StepMetric; a: FunnelSlot; b: FunnelSlot }> = [];
  steps.forEach((page, i) => {
    const slot = slotOf(page);
    if (!slot) return;   // a page not in a slot has no box to join (and no number to put on it)
    badges.push({ page, step: i + 1, x: slot.x - boxW / 2 + 6, y: slot.y - boxH / 2 + 6, w: 0, h: 0 });
    const next = i + 1 < steps.length ? steps[i + 1] : building ? null : goal;
    const nextSlot = next ? slotOf(next) : undefined;
    if (!next || !nextSlot) return;       // a ribbon joins TWO boxes: both must be on the grid
    const m = metric(page, next);
    if (!m || m.visits <= 0) return;      // nobody reached it (or the numbers have not arrived): nothing true to draw
    pairs.push({ page, next, step: i + 1, m, a: slot, b: nextSlot });
  });
  const maxVisits = pairs.reduce((mx, p) => Math.max(mx, p.m.visits), 0);
  const specs: RibbonSpec[] = pairs.map((p) => ({
    id: p.page,
    from: { key: p.page, x: p.a.x, y: p.a.y, w: boxW, h: boxH },
    to: { key: p.next, x: p.b.x, y: p.b.y, w: boxW, h: boxH },
    startW: ribbonWidth(p.m.visits, maxVisits, boxH),
    endW: ribbonWidth(p.m.wentOn, maxVisits, boxH),
  }));
  const shapes = layoutRibbons(specs);
  const items: DrawItem[] = pairs.map((p, i) => {
    const loss = lossOf(p.m);
    const level = levelOf(loss);

    return { page: p.page, next: p.next, step: p.step, totalSteps, metric: p.m, loss, level, colour: ribbonColour(level, byLoss), d: shapes[i].d, startW: shapes[i].startW, endW: shapes[i].endW, centre: shapes[i].centre, arriveAt: { x: p.b.x, y: p.b.y } };
  });
  // the boxes to dim: every slot that is not a step or the goal. While picking nothing is dimmed (the user needs to see every box to click one), and with no steps there is nothing to spotlight.
  const inFunnel = new Set(funnelPages(steps, goal, building));
  const dimKeys = spotlightActive(steps, building, dim) ? slots.filter((s) => !inFunnel.has(s.page)).map((s) => s.key) : [];
  // changes only when something visible would change (the path, the colour, the numbers), so redrawing on a timer costs nothing while nothing moves
  const signature = JSON.stringify({
    b: building,
    w: Math.round(boxW),
    h: Math.round(boxH),
    i: items.map((d) => [d.page, d.next, d.d, d.colour, Math.round(d.loss * 100), d.metric.visits, d.metric.wentOn, d.metric.left]),
    t: badges.map((d) => [d.page, d.step, Math.round(d.x), Math.round(d.y)]),
    x: dimKeys,
  });

  return { items, badges, dimKeys, signature };
};
